import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { useUserRoles } from '@anytag/auth';
import { SelectorField } from '@anytag/shared-molecules';
import { ButtonSubmit, Form } from '@anytag/shared-atoms';
import { useTiktokAdvertiserPlanDataQuery } from '@anytag/graphql';
import { formatPrice, localizedDateFormatter } from '@anytag/shared-utils';
import { useAllAnalyticsSubscriptionPlansOptions } from '@anytag/shared-hooks';
import { useAccountsFilterOptions } from '../../Accounts/AccountsFilter/hooks';
import { AccountManagerPlanFormValues, AccountManagerPlanFormKeys } from './types';

export const Template = () => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const {
    watch,
    formState: { isSubmitting, defaultValues }
  } = useFormContext<AccountManagerPlanFormValues>();
  const { data } = useTiktokAdvertiserPlanDataQuery({
    skip: !isAdminStaff || !defaultValues?.advertiserId,
    variables: { advertiserId: defaultValues?.advertiserId as number }
  });
  const { tikTokAdvertiserPlanOptions } = useAccountsFilterOptions();
  const { allSubscriptionPlanOptions } = useAllAnalyticsSubscriptionPlansOptions({
    hasNoPlanOption: !defaultValues?.accountManagerPlan
  });

  const accountManagerPlanValue = watch('accountManagerPlan');
  const tiktokPlan = data?.tiktokAdvertiserPlanData;

  return (
    <>
      <Form.Body className="form-container">
        <Form.SectionTitle label={t('Manage Account Plan')} />

        <Form.Fields itemsPerRow={2}>
          <SelectorField<AccountManagerPlanFormKeys>
            name="accountManagerPlan"
            title={t('Selector.Account Manager Plan')}
            options={allSubscriptionPlanOptions}
            required
          />
        </Form.Fields>

        {isAdminStaff && (
          <>
            <Form.SectionTitle label={t('TikTok Special Plan')} className="mt-4 mb-6" />
            {tiktokPlan ? (
              <div css={styles.tiktokPlan}>
                <div>
                  <p>{t('Plan Name')}</p>
                  <p>
                    {tikTokAdvertiserPlanOptions.find((option) => option.value === tiktokPlan.subscriptionPlan)
                      ?.label || '-'}
                  </p>
                </div>
                <div>
                  <p>{t('Plan Cost')}</p>
                  <p>
                    {formatPrice(tiktokPlan.planPrice, tiktokPlan.currencyId, 0)}
                    {typeof tiktokPlan.planPrice === 'number' ? tiktokPlan.currencyId : null}
                  </p>
                </div>
                <div>
                  <p>{t('Expiration Date')}</p>
                  <p>
                    {tiktokPlan.expirationDate ? localizedDateFormatter(tiktokPlan.expirationDate, 'yyyy/MM/dd') : '-'}
                  </p>
                </div>
                <div>
                  <p>{t('Payment Date')}</p>
                  <p>{tiktokPlan.paymentDate ? localizedDateFormatter(tiktokPlan.paymentDate, 'yyyy/MM/dd') : '-'}</p>
                </div>
              </div>
            ) : (
              <div css={{ fontSize: '14px' }}>{t('No plan')}</div>
            )}
          </>
        )}
      </Form.Body>

      <Form.Footer>
        <ButtonSubmit
          type="submit"
          title={t('Button.Save')}
          loading={isSubmitting}
          disabled={!accountManagerPlanValue}
        />
      </Form.Footer>
    </>
  );
};
const styles = {
  tiktokPlan: css({
    gap: '8px',
    display: 'grid',
    fontSize: '14px',
    gridTemplateColumns: 'repeat(4, 1fr)',
    '& div > p:last-of-type': { fontWeight: 600, marginTop: '4px' }
  })
};
